import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { ApiErrorResponse } from '@/types';
import { logApiError } from './utils';
import { setupMockInterceptors } from '../mockApi';
import { authService } from '@/services/auth';
import { authEvents } from '@/services/authEvents';
import { getConfigValue, isConfigEnabled } from '@/config/config';

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const apiUrl = getConfigValue('REACT_APP_API_URL') || 'https://localhost:5000/api/v1';
    config.baseURL = apiUrl;

    // Only add auth token if NOT using mock auth
    if (!isConfigEnabled('REACT_APP_USE_MOCK_AUTH')) {
      try {
        const token = await authService.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = token;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    }

    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`,
        'Headers:',
        config.headers
      );
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Setup mock interceptors for development
setupMockInterceptors(apiClient);

// Response interceptor with proper error typing
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error: AxiosError<ApiErrorResponse>) => {
    // Check if this is a mock response
    if (error.message === 'MOCK_RESPONSE' && error.response) {
      console.log(`✅ Mock Response: ${error.response.status} ${error.config?.url}`);
      return Promise.resolve(error.response);
    }

    // Log error using standardized utility
    logApiError(error, 'API Client');

    // Handle 401 Unauthorized and 403 Forbidden
    if (error.response?.status === 401 || error.response?.status === 403) {
      const currentPath = window.location.pathname;

      // Don't redirect if already on login or public pages
      if (
        currentPath === '/login' ||
        currentPath === '/auth-error' ||
        currentPath === '/unauthorized'
      ) {
        return Promise.reject(error);
      }

      const originalRequest = error.config as ExtendedAxiosRequestConfig;

      // Only retry once to avoid infinite loops
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        // For 403, it's likely a permission change - refresh permissions
        if (error.response.status === 403) {
          try {
            // Emit event to refresh permissions
            authEvents.emit('refreshPermissions');

            // Wait a bit for the refresh to complete
            await new Promise<void>((resolve) => setTimeout(() => { resolve(); }, 1000));

            // Retry the original request
            return apiClient(originalRequest);
          } catch (refreshError) {
            console.error('Permission refresh failed:', refreshError);
            // If permission refresh fails, user may need to re-login
            if (window.location.pathname !== '/unauthorized') {
              window.location.href = '/unauthorized';
            }
            return Promise.reject(error);
          }
        }

        // For 401, try to refresh the token first
        if (error.response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `${newToken}`;
              return apiClient(originalRequest);
            }
          } catch (refreshError) {
            // Token refresh failed, try permission refresh as last resort
            try {
              authEvents.emit('refreshPermissions');
              await new Promise<void>((resolve) => setTimeout(() => { resolve(); }, 1000));
              return apiClient(originalRequest);
            } catch (permissionError) {
              // Both token and permission refresh failed - sign out
              if (window.location.pathname !== '/login') {
                await authService.signOut();
                window.location.href = '/login';
              }
            }
          }
        }
      }

      // If already retried, handle based on status
      if (error.response?.status === 403) {
        // For 403 after retry, redirect to unauthorized page
        if (window.location.pathname !== '/unauthorized') {
          window.location.href = '/unauthorized';
        }
      } else if (error.response?.status === 401) {
        // For 401 after retry, sign out
        if (window.location.pathname !== '/login') {
          await authService.signOut();
          window.location.href = '/login';
        }
      }
    }

    if (error.response?.status >= 500) {
      // Server errors are already logged by logApiError
    }

    return Promise.reject(error);
  }
);

// API helper methods
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config).then((response) => response.data),

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config).then((response) => response.data),

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config).then((response) => response.data),

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config).then((response) => response.data),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config).then((response) => response.data),
};

export default apiClient;
